import { ScrollView, RefreshControl } from "react-native";
import { useState } from "react";
import { Redirect } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, Switch, Button } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useFeatureFlags, fetchFlags, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { isProductionEnvironment } from "@src/utils/environment";

export default function FeatureFlagsScreen() {
  const { theme } = useUnistyles();
  const {
    flags,
    localOverrides,
    isInitialized,
    isLoading,
    setFlagOverride,
    clearFlagOverride,
    clearAllFlagOverrides,
    hasFlagOverride,
    isFlagBooleanVariant,
    convertVariantToBoolean,
  } = useFeatureFlags();
  const [refreshing, setRefreshing] = useState(false);

  // Internal debug tooling — must never be reachable in the production app.
  if (isProductionEnvironment()) {
    return <Redirect href="/not-found" />;
  }

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchFlags();
    setRefreshing(false);
  };

  // Get the effective value of a flag (override > amplitude > default)
  const getEffectiveValue = (flagName: string) => {
    if (localOverrides[flagName] !== undefined) {
      return localOverrides[flagName];
    }
    return flags[flagName];
  };

  // Handle toggle for boolean flags (including string variants like "enabled"/"disabled")
  const handleToggle = (flagName: string) => {
    const currentValue = getEffectiveValue(flagName);
    const currentBoolean = convertVariantToBoolean(currentValue);
    const newValue = !currentBoolean;
    setFlagOverride(flagName, newValue);
  };

  // Check if a flag is a boolean-type flag (including string variants)
  const isBooleanFlag = (value: any): boolean => {
    return isFlagBooleanVariant(value);
  };

  return (
    <View flex={1} bg="background">
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: theme.spacing["3xl"],
          paddingBottom: theme.spacing["6xl"],
        }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.interactive.buttons.default_background}
          />
        }
      >
        {/* Status Section */}
        <View mb="4xl" p="3xl" bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius}>
          <Text textStyle="sm/bold" style={{ marginBottom: theme.spacing["3xl"] }}>
            Feature Flags Status
          </Text>

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: theme.spacing.lg,
            }}
          >
            <Text textStyle="sm/medium" style={{ color: theme.colors.textMuted }}>
              Initialized:
            </Text>
            <Text
              textStyle="sm/bold"
              style={{
                color: isInitialized ? theme.colors.status.success : theme.colors.status.error,
              }}
            >
              {isInitialized ? "Yes" : "No"}
            </Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: theme.spacing.lg,
            }}
          >
            <Text textStyle="sm/medium" style={{ color: theme.colors.textMuted }}>
              Loading:
            </Text>
            <Text textStyle="sm/bold">{isLoading ? "Yes" : "No"}</Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: Object.keys(localOverrides).length > 0 ? theme.spacing.lg : 0,
            }}
          >
            <Text textStyle="sm/medium" style={{ color: theme.colors.textMuted }}>
              Local Overrides:
            </Text>
            <Text textStyle="sm/bold">{Object.keys(localOverrides).length}</Text>
          </View>

          {Object.keys(localOverrides).length > 0 && (
            <Button
              label="Clear All Overrides"
              variant="secondary"
              size="sm"
              onPress={clearAllFlagOverrides}
              style={{ marginTop: theme.spacing.md }}
            />
          )}
        </View>

        {/* All Flags Section */}
        <View mb="4xl" p="3xl" bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius}>
          <Text textStyle="sm/bold" style={{ marginBottom: theme.spacing["3xl"] }}>
            All Flags
          </Text>

          {Object.keys(flags).length === 0 ?
            <Text textStyle="xs/medium" style={{ color: theme.colors.textMuted, fontStyle: "italic" }}>
              No flags loaded yet. Pull to refresh to fetch flags.
            </Text>
          : Object.entries(flags).map(([key, value], index) => {
              const effectiveValue = getEffectiveValue(key);
              const isOverridden = hasFlagOverride(key);
              const isBoolean = isBooleanFlag(effectiveValue);

              return (
                <View
                  key={key}
                  style={{
                    paddingVertical: theme.spacing.lg,
                    borderBottomWidth: index < Object.keys(flags).length - 1 ? 1 : 0,
                    borderBottomColor: theme.colors.border,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text textStyle="sm/bold">{key}</Text>
                      {isOverridden && (
                        <Text
                          textStyle="xs/medium"
                          style={{
                            color: theme.colors.interactive.buttons.default_background,
                            marginTop: theme.spacing.xs,
                          }}
                        >
                          Local Override (Amplitude: {JSON.stringify(flags[key])})
                        </Text>
                      )}
                    </View>

                    {isBoolean ?
                      <View style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.md }}>
                        {isOverridden && (
                          <Button
                            label="Reset"
                            variant="link"
                            size="sm"
                            onPress={() => clearFlagOverride(key)}
                            style={{ paddingHorizontal: theme.spacing.sm, minWidth: 60 }}
                          />
                        )}
                        <Switch
                          active={convertVariantToBoolean(effectiveValue)}
                          onToggle={() => handleToggle(key)}
                          size="small"
                        />
                      </View>
                    : <Text
                        textStyle="sm/regular"
                        style={{
                          color: theme.colors.interactive.buttons.secondary_foreground,
                        }}
                      >
                        {JSON.stringify(effectiveValue)}
                      </Text>
                    }
                  </View>
                </View>
              );
            })
          }
        </View>

        {/* Known Flags Section */}
        <View mb="4xl" p="3xl" bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius}>
          <Text textStyle="sm/bold" style={{ marginBottom: theme.spacing["3xl"] }}>
            Known Flags
          </Text>

          {Object.entries(FEATURE_FLAGS).map(([name, flagName]) => {
            const key = flagName;
            const effectiveValue = getEffectiveValue(key);
            const isOverridden = hasFlagOverride(key);
            const isBoolean = isBooleanFlag(effectiveValue);
            const isSet = effectiveValue !== undefined;

            return (
              <View
                key={key}
                style={{
                  paddingVertical: theme.spacing.lg,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text textStyle="sm/bold">{key}</Text>
                    {isOverridden && (
                      <Text
                        textStyle="xs/medium"
                        style={{
                          color: theme.colors.interactive.buttons.default_background,
                          marginTop: theme.spacing.xs,
                        }}
                      >
                        Local Override (Amplitude: {JSON.stringify(flags[key] ?? "Not set")})
                      </Text>
                    )}
                  </View>

                  {!isSet ?
                    <Text
                      textStyle="sm/regular"
                      style={{
                        color: theme.colors.textMuted,
                        fontStyle: "italic",
                      }}
                    >
                      Not set
                    </Text>
                  : isBoolean ?
                    <View style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.md }}>
                      {isOverridden && (
                        <Button
                          label="Reset"
                          variant="link"
                          size="sm"
                          onPress={() => clearFlagOverride(key)}
                          style={{ paddingHorizontal: theme.spacing.sm, minWidth: 60 }}
                        />
                      )}
                      <Switch
                        active={convertVariantToBoolean(effectiveValue)}
                        onToggle={() => handleToggle(key)}
                        size="small"
                      />
                    </View>
                  : <Text
                      textStyle="sm/regular"
                      style={{
                        color: theme.colors.interactive.buttons.secondary_foreground,
                      }}
                    >
                      {JSON.stringify(effectiveValue)}
                    </Text>
                  }
                </View>
              </View>
            );
          })}
        </View>

        {/* Hint */}
        <View style={{ alignItems: "center" }}>
          <Text textStyle="xs/medium" style={{ color: theme.colors.textMuted, fontStyle: "italic" }}>
            Pull down to refresh flags from Amplitude
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}
